<template id="RepoTable">
  <div id="repo-details">
    <b-row class="my-1">
      <b-col>
        <b-button
          variant="primary"
          :disabled="selected.length <= 0"
          v-b-modal.export-modal
        >
          Export
        </b-button>
        <b-modal id="export-modal" title="Export Data">
          <b-row>
            <b-col cols="6">
              Data filter start:
              <b-form-datepicker
                id="example-datepicker"
                v-model="timeFilterStart"
                :max="timeFilterEnd"
                :date-format-options="dateSelectFormat"
                value-as-date
              />
            </b-col>
            <b-col cols="6">
              Data filter end:
              <b-form-datepicker
                id="example-datepickera"
                v-model="timeFilterEnd"
                :min="timeFilterStart"
                :max="new Date()"
                :date-format-options="dateSelectFormat"
                value-as-date
              />
            </b-col>
          </b-row>

          <template #modal-footer="{ cancel }">
            <b-button variant="primary" @click="exportAll">
              Download
            </b-button>
            <b-button @click="cancel()">
              Cancel
            </b-button>
          </template>
        </b-modal>
      </b-col>
      <b-col>
        <b-form-group
          label="Filter"
          label-cols-sm="8"
          label-align-sm="right"
          label-size="sm"
          label-for="filterInput"
          class="mb-0"
        >
          <b-input-group size="sm">
            <b-form-input
              v-model="filter"
              type="search"
              id="filterInput"
              placeholder="Type to Search"
            />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <b-form-checkbox-group v-model="selected">
      <b-table
        sticky-header="640px"
        striped
        hover
        no-border-collapse
        primary-key="uniqueKey"
        :items="items"
        :filter="filter"
        :fields="fields"
        sort-by="updatedAt"
        :sort-desc="true"
      >
        <template v-slot:head(private)>
          <privacy-icon :isPublic="false" />
        </template>
        <template v-slot:head(forkCount)>
          <fork-icon />
        </template>
        <template v-slot:head(starCount)>
          <star-icon />
        </template>
        <template v-slot:head(watcherCount)>
          <watch-icon />
        </template>

        <template v-slot:cell(selectInfo)="data">
          <b-form-checkbox class="checkBox" :value="data.value" />
        </template>
        <template v-slot:cell(name)="data">
          <router-link :to="`/org/${data.item.orgid}/repo/${data.item.repoid}`">
            {{ data.value }}
          </router-link>
        </template>
        <template v-slot:cell(compliant)="data">
          <compliance-icon :complianceState="data.value" />
        </template>
        <template v-slot:cell(private)="data">
          <privacy-icon :isPublic="data.value" />
        </template>

        <template v-slot:table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="{
              width:
                field.key === 'private' ||
                field.key === 'forkCount' ||
                field.key === 'starCount' ||
                field.key === 'watcherCount'
                  ? '50px'
                  : field.key === 'compliant'
                  ? '100px'
                  : field.key === 'selectInfo'
                  ? '10px'
                  : '180px',
            }"
          />
        </template>

        <template #empty="scope">
          {{ scope.emptyText }}
        </template>
      </b-table>
    </b-form-checkbox-group>
  </div>
</template>

<script>
import { createRepoMetricsCSVBlob, zippedExport } from "../common/export_utils";
import { yearMonthDayFormat } from "../common/time_utils";
import {
  ComplianceIcon,
  ForkIcon,
  PrivacyIcon,
  StarIcon,
  WatchIcon,
} from "./icons";

export default {
  name: "RepoTable",
  props: {
    repos: { type: Array },
    excludeColumns: {
      type: Array,
    },
  },
  components: {
    ComplianceIcon,
    PrivacyIcon,
    ForkIcon,
    StarIcon,
    WatchIcon,
  },
  data: function() {
    return {
      filter: null,
      selected: [],
      defaultFields: [
        // Used to select repositories for export
        { key: "selectInfo", label: "" },
        { key: "name", sortable: true },
        { key: "organization", sortable: true },
        "compliant",
        "private",
        { key: "forkCount", sortable: true },
        { key: "starCount", sortable: true },
        { key: "watcherCount", sortable: true },
        { key: "createdAt", label: "Created", sortable: true },
        { key: "updatedAt", label: "Updated", sortable: true },
        { key: "pushedAt", label: "Pushed", sortable: true },
      ],
      timeFilterStart: new Date(),
      timeFilterEnd: new Date(),
      dateSelectFormat: {
        year: "numeric",
        month: "short",
        day: "2-digit",
        weekday: "short",
      },
    };
  },
  methods: {
    async exportAll() {
      const exportBlobs = await Promise.all(
        this.selected.map(async (repo) => {
          let copyStart = new Date(this.timeFilterStart);
          let copyEnd = new Date(this.timeFilterEnd);
          copyStart.setHours(0, 0, 0, 0);
          copyEnd.setHours(23, 59, 59, 999);
          const metrics = await this.$client.get("repositories/" + repo.id, {
            parameters: {
              startTime: copyStart.toISOString(),
              endTime: copyEnd.toISOString(),
            },
          });

          return {
            name: repo.name + ".csv",
            content: createRepoMetricsCSVBlob(repo.name, metrics),
          };
        })
      );

      zippedExport("export_files.zip", exportBlobs);
    },
  },
  computed: {
    items: function() {
      if (this.repos) {
        return this.repos.map((current) => {
          return {
            ...current,
            uniqueKey: `${current.orgid}-${current.repoid}`,
            createdAt: yearMonthDayFormat(current.createdAt),
            updatedAt: yearMonthDayFormat(current.updatedAt),
            pushedAt: yearMonthDayFormat(current.pushedAt),
            selectInfo: { id: current.repoid, name: current.name },
          };
        });
      } else {
        return [];
      }
    },
    fields: function() {
      return this.defaultFields.filter((item) => {
        if (!this.excludeColumns) {
          return true;
        }
        const key = item.key || item;

        return !this.excludeColumns.includes(key);
      });
    },
  },
  created() {
    // Sets timeFilterStart to be 1 month before todays date
    this.timeFilterStart.setMonth(this.timeFilterStart.getMonth() - 1);
  },
};
</script>

<style scoped>
.expandIcon {
  float: right;
}
.checkBox {
  margin: 0px;
}
</style>
